import { X } from 'lucide-react';
import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useApplyReferral } from '@/hooks/useUsers';

export default function ApplyReferralModal({
  isOpen,
  onClose,
  userId,
}: {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
}) {
  const [referralCode, setReferralCode] = useState('');
  const applyReferral = useApplyReferral();
  const toast = useToast();

  if (!isOpen) return null;

  const handleClose = () => {
    setReferralCode('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!referralCode.trim())
      return toast.error('Invalid code', 'Enter a referral code');
    try {
      await applyReferral.mutateAsync({
        userId,
        referral_code: referralCode.trim(),
      });
      toast.success('Referral applied successfully');
      handleClose();
    } catch (e) {
      toast.error(
        'Failed to apply referral',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:items-center"
      onClick={handleClose}
    >
      <Card
        className="my-auto w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Apply Referral</CardTitle>
              <CardDescription>
                Link this user to a referrer using their referral code
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">
              Referral Code
            </label>
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              placeholder="Enter referral code"
              className="w-full px-3 py-2 border rounded-md bg-background font-mono"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={applyReferral.isPending || !referralCode.trim()}
              className="flex-1"
            >
              {applyReferral.isPending ? 'Applying...' : 'Apply Referral'}
            </Button>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
